// The login page of a locked relay site, /login (server/README.md "Site lock"): every other page sends a browser
// that isn't logged in here (Caddy asks the relay, which redirects to /login?next=<the page>). The password is the
// admin page's (POST /api/support/admin/login: an HttpOnly cookie for the whole site, 30 days). Afterwards it goes
// back to the page, and first says so when wrong passwords were tried since the last login.

import { loginNext } from '../core/shell';
import { BrokerClient, brokerMessage, failedLoginsText } from '../core/support';
import { ago, h, icon, logo, replace } from '../ui/dom';

const app = document.getElementById('app')!;
const broker = new BrokerClient('', { kind: 'admin' });
const next = loginNext(location.search, location.hash);

function shell(...body: Array<Node | null>): void {
  replace(app, h('header', { class: 'doc-head' }, logo(28), h('span', { class: 'doc-brand' }, 'MonkViewer')), body);
}

function go(): void {
  location.replace(next);
}

function showLogin(message = ''): void {
  document.body.dataset.view = 'login';
  const pw = h('input', { class: 'input', type: 'password', id: 'site-password', autocomplete: 'current-password', 'aria-label': 'Password', maxLength: 256 });
  const err = h('div', { class: 'error-text', role: 'alert', id: 'login-error' }, message);
  const btn = h('button', { class: 'btn primary block', id: 'login', type: 'submit' }, 'Log in');
  const form = h('form', { class: 'doc-card login-card', autocomplete: 'on' },
    h('h1', { class: 'doc-title' }, 'Log in'),
    h('p', { class: 'muted' }, "This site is private. Use the relay server's admin password."),
    h('input', { type: 'text', name: 'username', value: 'admin', autocomplete: 'username', hidden: true, 'aria-hidden': 'true' }),
    h('div', { class: 'field' }, h('label', { htmlFor: 'site-password' }, 'Password'), pw),
    err, btn);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!pw.value) return;
    btn.disabled = true;
    err.textContent = '';
    try {
      const r = await broker.loginInfo(pw.value);
      pw.value = '';
      const note = failedLoginsText(r.failed, (t) => ago(t * 1000));
      if (!note) {
        go();
        return;
      }
      // Wrong passwords were tried: say so once before going on (this login is the one that sees them).
      document.body.dataset.view = 'failed';
      shell(h('div', { class: 'doc-card login-card' },
        h('h1', { class: 'doc-title' }, 'Logged in'),
        h('div', { class: 'notice', id: 'failed-logins', role: 'status' }, icon('alert'), h('span', null, note)),
        h('p', { class: 'hint' }, 'If that wasn\'t you, change the password on the admin page (/admin). Every address gets only a few tries.'),
        h('button', { class: 'btn primary block', id: 'continue', onclick: go }, 'Continue')));
      document.getElementById('continue')?.focus();
    } catch (x) {
      err.textContent = brokerMessage(x);
      btn.disabled = false;
      pw.select();
    }
  });
  shell(form);
  pw.focus();
}

async function boot(): Promise<void> {
  shell(h('div', { class: 'progress' }, h('div', { class: 'spinner', 'aria-hidden': 'true' })));
  try {
    if ((await broker.session()) > 0) {
      go(); // already logged in (another tab, or the back button)
      return;
    }
    showLogin();
  } catch (e) {
    shell(h('div', { class: 'doc-card' }, h('h1', { class: 'doc-title' }, "Can't reach the server"), h('p', { class: 'muted' }, brokerMessage(e)),
      h('button', { class: 'btn primary', onclick: () => void boot() }, icon('refresh'), 'Try again')));
  }
}

void boot();
